import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ResumerSaveDialogBoxComponent } from '../resumer-save-dialog-box/resumer-save-dialog-box.component';
import { ResumerCheckScoreComponent } from '../resumer-check-score/resumer-check-score.component';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { ToastrService } from 'ngx-toastr';
import { ResumeDataByID } from '@edtech-ui/core/models/resume-builder/get-resume-by-id';
import { ClassicComponent } from '../resume-template/classic/classic.component';
import { ModernComponent } from '../resume-template/modern/modern.component';
import { SidebarComponent } from '../resume-template/sidebar/sidebar.component';
import html2pdf from 'html2pdf.js';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MinimalTemplateComponent } from '../resume-template/minimal-template/minimal-template.component';
import { ProfessionalTemplateComponent } from '../resume-template/professional-template/professional-template.component';
import { ExecutiveTemplateComponent } from '../resume-template/executive-template/executive-template.component';
import { CompactTemplateComponent } from '../resume-template/compact-template/compact-template.component';
import { CreativeTemplateComponent } from '../resume-template/creative-template/creative-template.component';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';

@Component({
	selector: 'app-resumer-preview',
	standalone: true,
	imports: [CommonModule, FormsModule, RouterModule, ResumerCheckScoreComponent,
		ClassicComponent, ModernComponent, SidebarComponent, HasPermissionDirective,
		MatProgressSpinnerModule , MinimalTemplateComponent,ProfessionalTemplateComponent,
		ExecutiveTemplateComponent,CompactTemplateComponent,CreativeTemplateComponent
	],
	templateUrl: './resumer-preview.component.html',
	styleUrl: './resumer-preview.component.scss'
})
export class ResumerPreviewComponent implements OnInit, OnDestroy {

	selectedTemplate: number = 0;
	selectedFont: string = 'Times New Roman';
	showScore = false;
	subscription = new Subscription();
	studentResumeDetails: ResumeDataByID | null = null;
	@Input() resumeId: string = '';
	resumeName: string = '';

	@ViewChild(ResumerCheckScoreComponent)
	scoreComp!: ResumerCheckScoreComponent;
	PermissionForStudents = PermissionForStudents;
	selectedColor: string = '#000000';
	isDownloading = false;
	isLoading=false;
	returnUrl: string | null = null;
	isApplyingToJob = false;


	constructor(
		private dialog: MatDialog,
		private resumeService: ResumeService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthServiceService
	) { }

	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.resumeId = params.get('resumeId') || ''
			if (this.resumeId) {
				this.getStudentResumes();
			}
		});

		this.subscription.add(
			this.route.queryParams.subscribe(params => {
				if (params['returnUrl']) {
					this.returnUrl = params['returnUrl'];
				}
			})
		);
	}


	getStudentResumes() {
		this.isLoading=true;
		this.subscription.add(
			this.resumeService.getStudentResumeById(this.resumeId).pipe(
				tap(response => {
					if (response.Success) {
						this.studentResumeDetails = response.Data;
						this.resumeName = response.Data.resume_name || 'Untitle Resume';
						this.selectedColor = response.Data.config.themeColor || '#000000';
						this.selectedFont = response.Data.config.fontFamily || 'Inter';
						const templateId = response.Data.config.templateId;

						const templateIndex = this.templates.findIndex(
							t => t.name.toLowerCase() === templateId?.toLowerCase());

							this.selectedTemplate = templateIndex !== -1 ? templateIndex : 0;

					}
				}),
				finalize(()=>{
					this.isLoading=false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		)
	}


	saveResumeName(name: string) {
		const payload = {
			resume_name: name
		}
		this.subscription.add(
			this.resumeService.updateStudentResume(this.resumeId, payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success('Resume name saved successfully');
						this.getStudentResumes();
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		);
	}


	private async toBase64DataUrl(url: string): Promise<string> {
		try {
			const res = await fetch(url, { mode: 'cors', cache: 'no-cache' });
			const blob = await res.blob();
			return new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			});
		} catch (e) {
			console.error('Failed to convert image to base64:', e);
			return url;
		}
	}

	private async replaceImagesWithBase64(element: HTMLElement): Promise<() => void> {
		const images = Array.from(element.querySelectorAll('img')) as HTMLImageElement[];
		const originals: string[] = images.map(img => img.src);

		await Promise.all(
			images.map(async (img) => {
				const src = img.getAttribute('src') || img.src || '';
				if (!src || src.startsWith('data:')) return;
				const b64 = await this.toBase64DataUrl(src);
				img.src = b64;
				if (!img.complete) {
					await new Promise((resolve) => {
						img.onload = resolve;
						img.onerror = resolve;
					});
				}
			})
		);

		return () => {
			images.forEach((img, i) => {
				img.src = originals[i];
			});
		};
	}

	async downloadPDF() {
		const element = document.querySelector('.preview') as HTMLElement;
		if (!element) return;
		this.isDownloading = true;

		let restoreImages: (() => void) | null = null;
		try {
			restoreImages = await this.replaceImagesWithBase64(element);

			const options = {
				margin: 0.5,
				filename: `${this.resumeName}.pdf`,
				image: { type: 'jpeg' as const, quality: 0.98 },
				html2canvas: { scale: 2, useCORS: true, allowTaint: true },
				jsPDF: { unit: 'in' as const, format: 'a4' as const, orientation: 'portrait' as const }
			};

			await html2pdf().set(options).from(element).save();
		} catch (e) {
			console.error('Error exporting PDF:', e);
		} finally {
			if (restoreImages) restoreImages();
			this.isDownloading = false;
		}
	}

	async useResumeToApply() {
		const element = document.querySelector('.preview') as HTMLElement;
		if (!element) return;
		this.isApplyingToJob = true;

		let restoreImages: (() => void) | null = null;
		try {
			restoreImages = await this.replaceImagesWithBase64(element);

			const options = {
				margin: 0.5,
				filename: `${this.resumeName}.pdf`,
				image: { type: 'jpeg' as const, quality: 0.98 },
				html2canvas: { scale: 2, useCORS: true, allowTaint: true },
				jsPDF: { unit: 'in' as const, format: 'a4' as const, orientation: 'portrait' as const }
			};

			const blob = await html2pdf().set(options).from(element).outputPdf('blob');
			const file = new File([blob], `${this.resumeName || 'Resume'}.pdf`, { type: 'application/pdf' });

			const formData = new FormData();
			formData.append('resume', file);

			this.subscription.add(
				this.authService.patchUserEditProfile(formData).pipe(
					tap((res: any) => {
						if (res?.Success) {
							this._toastr.success('Resume updated on your profile.');
							if (this.returnUrl) {
								this.router.navigateByUrl(this.returnUrl);
							} else {
								this.router.navigate(['/student-dashboard/resume']);
							}
						} else {
							this._toastr.error(res?.Message || 'Failed to update resume on profile.');
						}
					}),
					finalize(() => {
						this.isApplyingToJob = false;
					}),
					catchError(() => {
						this._toastr.error('Failed to upload resume to profile.');
						return of(null);
					})
				).subscribe()
			);
		} catch (err) {
			this._toastr.error('Failed to generate resume PDF.');
			this.isApplyingToJob = false;
		} finally {
			if (restoreImages) restoreImages();
		}
	}


	getSelectedTemplate() {
		switch (this.selectedTemplate) {
			case 0: return 'classic';
			case 1: return 'modern';
			case 2: return 'sidebar';
			default: return 'classic';
		}
	}

	fonts: string[] = [
		'Inter',
		'Georgia',
		'Arial',
		'Times New Roman',
		'Calibri',
		'Garamond', 'Helvetica'
	];


	changeFont(font: string) {
		this.selectedFont = font;
	}


	selectTemplate(index: number) {
		this.selectedTemplate = index;
	}


	openSaveResumeDialogBox() {
		const dialogRef = this.dialog.open(ResumerSaveDialogBoxComponent,{
			data:{
				resumeName:this.resumeName
			}
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				const name = result;
				this.saveResumeName(name);
			}
		});
	}


	colors: string[] =['#1e293b', '#1e3a5f', '#475569', '#2563eb', '#166534', '#0d9488', '#881337', '#6b21a8', '#78350f', '#374151', '#0f172a'];

	selectColor(color: string) {
		this.selectedColor = color;
	}

	templates = [
		{ name: 'Classic', layout: 'classic',type: 'one-col'  },
		{ name: 'Modern', layout: 'modern' ,type: 'one-col' },
		{ name: 'Minimal', layout: 'minimal' ,type: 'one-col' },
		{ name: 'Professional', layout: 'professional',type: 'one-col'  },
		{ name: 'Sidebar', layout: 'sidebar' ,type: 'two-col' },
		{ name: 'Executive',layout:'executive' ,type: 'two-col' },
		{ name: 'Creative', layout: 'creative' ,type: 'two-col' },
		{ name: 'Compact', layout: 'compact',type: 'two-col' } ,
	];


	showScoreAndAnalyze() {
		this.showScore = true;

		setTimeout(() => {
			if (this.scoreComp) {
				this.scoreComp.postAnalyzeScore();
			}
		});
	}

	goToEditPage(resumeId: string) {
		const navExtras: any = {};
		if (this.returnUrl) {
			navExtras.queryParams = { returnUrl: this.returnUrl };
		}
		this.router.navigate(['/student-dashboard/resume-builder', resumeId], navExtras);
	}


	applyTemplate(layout: string) {
	}

	handleCloseScore() {
		 this.showScore = false; 
	}
	

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}